/* LoadingScreen.jsx */
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import LOGO      from "../images/logo/record.png";
import vhiSwish  from "../music/tag/vhi swish.wav";
import smokeGIF  from "../images/logo/Smoke.gif";

import DigitalRain             from "./DigitalRain";
import useDynamicLoadingText   from "../hooks/useDynamicLoadingText";
import {withPublicCatalogPreview} from "../lib/publicCatalogPreview";

/* ───── Skip flag – eval’d once at module load ───────── */
const SKIP_LOADER = sessionStorage.getItem("vkzSkipLoader") === "1";
if (SKIP_LOADER) sessionStorage.removeItem("vkzSkipLoader");

/* ───── helpers (unchanged) ──────────────────────────── */
const LOADER_TOTAL_MS = 3000;
const LOADER_TEXT_MS  = 800;
const LOADER_TEXT_WAIT_MS = 1200;
const LOADER_FADE_MS  = 600;
const LOADER_FADE_AT  = Math.max(0, LOADER_TOTAL_MS - LOADER_FADE_MS);
const LOADER_SKIP_MS  = 300;
const LOADER_AUTO_SKIP_MS = 3000;
const SHRINK = 0.25;
function headlineFS(view, len) {
  const { baseVW, minVW, maxREM } = LS_LAYOUT[view];
  const dynVW = Math.max(minVW, baseVW - len * SHRINK);
  return `clamp(1rem, ${dynVW}vw, ${maxREM}rem)`;
}
const waveFS = headlineFS;

const fadeOutAudio = (audio) => {
  if (!audio) return;
  const steps = 6;
  const intervalMs = Math.max(60, Math.floor(LOADER_FADE_MS / steps));
  const step = Math.max(0.08, audio.volume / steps);
  const iv = setInterval(() => {
    audio.volume = Math.max(0, audio.volume - step);
    if (audio.volume === 0) { clearInterval(iv); audio.pause(); }
  }, intervalMs);
};
const clearAll = (arr) => arr.forEach((id) => clearTimeout(id));
const vis = (ch) => (ch === " " ? "\u00A0" : ch);
const isAsciiPrintable = (ch) => /^[\x20-\x7E]$/.test(ch);
const FONT_ASCII =
  '"Oxanium","Segoe UI",system-ui,-apple-system,sans-serif';
const FONT_UNICODE =
  '"Oxanium","Noto Sans","Noto Sans CJK SC","Noto Sans JP","Noto Sans KR","PingFang SC","Hiragino Sans","Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",system-ui,-apple-system,sans-serif';
const normalizeLoadingText = (raw, fallback) => {
  const cleaned = String(raw ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || fallback;
};

const BP = { mobile:0, tablet:768, desktop:1024, ultra:2560 };
const getView = () => {
  const w   = window.innerWidth;
  const isL = window.matchMedia("(orientation: landscape)").matches;
  if (w >= BP.ultra)   return "ultra";
  if (w >= BP.desktop) return "desktop";
  if (w >= BP.tablet)  return "tablet";
  return isL ? "mobileL" : "mobileP";
};

/* breakpoint layout tokens */
const LS_LAYOUT = {
  mobileP:{ baseVW:12,minVW:6,maxREM:12,
    headlineTop:"50%",headlineLeft:".2rem",
    headlineRight:"0",headlineBottom:"auto",
    copyLeft:"51%",copyTop:"-3.5rem",copyRight:"auto",copyBottom:"0",
    logoWidth:"40vw",logoBottom:"15%" },
  mobileL:{ baseVW:10,minVW:5,maxREM:10,
    headlineTop:"150%",headlineLeft:".2rem",
    headlineRight:"0",headlineBottom:"auto",
    copyLeft:"50%",copyTop:"-3rem",copyRight:"auto",copyBottom:"0",
    logoWidth:"35vw",logoBottom:"14%" },
  tablet :{ baseVW:10,minVW:5,maxREM:12,
    headlineTop:"147%",headlineLeft:".2rem",
    headlineRight:"0",headlineBottom:"auto",
    copyLeft:"48%",copyTop:"0",copyRight:"auto",copyBottom:"0",
    logoWidth:"30vw",logoBottom:"13%" },
  desktop:{ baseVW:10,minVW:4.5,maxREM:14,
    headlineTop:"142.7%",headlineLeft:"0",
    headlineRight:"0",headlineBottom:"auto",
    copyLeft:"50.8%",copyTop:"5.7rem",copyRight:"auto",copyBottom:"0",
    logoWidth:"clamp(120px, 45vw, 400px)",logoBottom:"12%" },
  ultra  :{ baseVW:6,minVW:3,maxREM:16,
    headlineTop:"298%",headlineLeft:"-3.3rem",
    headlineRight:"0rem",headlineBottom:"auto",
    copyLeft:"40%",copyTop:"0",copyRight:"auto",copyBottom:"0",
    logoWidth:"clamp(200px, 50vw, 600px)",logoBottom:"10%" },
};

export default function LoadingScreen({ tracks, onFinish }) {
  /* ───── hooks declared UNCONDITIONALLY ─────────────── */
  const [view, setView]       = useState(getView);
  const loadingText           = useDynamicLoadingText();
  const loadingTextReady      = loadingText != null;
  const displayText           = normalizeLoadingText(loadingText, "");
  const displayChars          = useMemo(() => Array.from(displayText), [displayText]);
  const canClipText = useMemo(() => {
    if (typeof window === "undefined" || !window.CSS || !window.CSS.supports) return false;
    return (
      window.CSS.supports("background-clip", "text") ||
      window.CSS.supports("-webkit-background-clip", "text")
    );
  }, []);
  // Canvas rain should stay ASCII-safe.
  const rainText = useMemo(() => {
    const ascii = displayText.replace(/[^\x20-\x7E]/g, "").trim();
    return ascii || "VONTHEKEYZ";
  }, [displayText]);

  const [showLogo, setShowLogo]   = useState(false);
  const [fadeScreen, setFadeScreen] = useState(false);
  const [fadeText, setFadeText]   = useState(false);
  const [skipped, setSkipped]     = useState(false);
  const [timelineStarted, setTimelineStarted] = useState(false);

  const rndAudio   = useRef(null);
  const swishAudio = useRef(null);
  const timers     = useRef([]);
  const startedRef = useRef(false);
  const finishedRef = useRef(false);

  const randColor    = () => "#" + Math.floor(Math.random()*0xffffff).toString(16).padStart(6,"0");
  const randGradient = () => `linear-gradient(45deg, ${randColor()}, ${randColor()})`;

  const headlineGradient = useMemo(
    () => (displayText ? randGradient() : "linear-gradient(90deg, #fff, #fff)"),
    [displayText]
  );
  const hasNonAscii = useMemo(
    () => displayChars.some((ch) => !isAsciiPrintable(ch)),
    [displayChars]
  );

  const copies = useMemo(() => {
    if (!displayChars.length) return [];
    const arr = [], NUM = 3;
    for (let i=0;i<NUM;i++){
      arr.push({
        fontSize:`${1+Math.random()*2}rem`,
        duration:`${1+Math.random()*2}s`,
        delay:Math.random()*0.5,
        letterDelay:1+Math.random()*1.2,
        colors:displayChars.map(randColor),
      });
    }
    return arr;
  }, [displayChars]);

  const finishLoader = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    try { window.dispatchEvent(new Event("vkz-loading-done")); } catch {}
    onFinish?.();
  }, [onFinish]);

  /* ───── universal skip handler (tap / click) ───────── */
  const handleSkip = useCallback(() => {
    if (skipped || finishedRef.current) return;
    setSkipped(true);
    clearAll(timers.current);
    setFadeText(true);
    setFadeScreen(true);
    [rndAudio.current, swishAudio.current].forEach(fadeOutAudio);
    setTimeout(() => finishLoader(), LOADER_SKIP_MS);
  }, [skipped, finishLoader]);

  /* ───── effects ────────────────────────────────────── */
  /* Resize listener – harmless even if we skip */
  useEffect(() => {
    const resize = () => setView(getView());
    window.addEventListener("resize", resize);
    window.matchMedia("(orientation: landscape)")
          .addEventListener("change", resize);
    return () => {
      window.removeEventListener("resize", resize);
    };
  }, []);

  /* Early-finish when skip flag is set */
  useEffect(() => {
    if (startedRef.current) return;

    if (SKIP_LOADER) {
      startedRef.current = true;
      finishLoader();         // run once, immediately
      return;                 // skip the rest of the effect body
    }

    let waitId;

    const startTimeline = () => {
      if (startedRef.current) return;
      startedRef.current = true;

      /* SFX */
      swishAudio.current = new Audio(vhiSwish);
      swishAudio.current.volume = 0.3;
      swishAudio.current.play().catch(()=>{});

      if (tracks?.length){
        const t = withPublicCatalogPreview(tracks[Math.floor(Math.random()*tracks.length)] || {});
        if (t?.audioURL){
          rndAudio.current = new Audio(t.audioURL);
          rndAudio.current.volume = 0.3;
          rndAudio.current.play().catch(()=>{});
        }
      }

      /* timeline */
      const finishId = setTimeout(() => {
        setFadeScreen(true);
        document.body.classList.replace("preload-bg","fade-preload");
        [rndAudio.current, swishAudio.current].forEach(fadeOutAudio);
        timers.current.push(setTimeout(() => finishLoader(), LOADER_FADE_MS));
      }, LOADER_FADE_AT);

      setTimelineStarted(true);
      timers.current.push(finishId);
    };

    if (loadingTextReady) {
      startTimeline();
    } else {
      waitId = setTimeout(startTimeline, LOADER_TEXT_WAIT_MS);
    }

    return () => {
      clearTimeout(waitId);
      clearAll(timers.current);
      rndAudio.current?.pause();
      swishAudio.current?.pause();
    };
  }, [tracks, loadingTextReady, finishLoader]);

  useEffect(() => {
    if (SKIP_LOADER || skipped) return;
    const id = setTimeout(() => handleSkip(), LOADER_AUTO_SKIP_MS);
    return () => clearTimeout(id);
  }, [skipped, handleSkip]);

  // Tie the text/logo fade to when the display text is known (or updated).
  useEffect(() => {
    if (!timelineStarted) return;
    setFadeText(false);
    setShowLogo(false);
    const id = setTimeout(() => { setFadeText(true); setShowLogo(true); }, LOADER_TEXT_MS);
    return () => clearTimeout(id);
  }, [displayText, timelineStarted]);

  /* ───── render ─────────────────────────────────────── */
  if (SKIP_LOADER) return null;

  return (
    <div
      role="button"
      onClick={handleSkip}
      onKeyDown={(e)=>e.key==='Enter'&&handleSkip()}
      tabIndex={0}
      style={{
        position:"fixed", inset:0,
        background:"linear-gradient(to top, black, #6900f8)",
        overflow:"hidden", zIndex:99,
        transition:`opacity ${LOADER_FADE_MS}ms ease`,
        opacity: fadeScreen ? 0 : 1,
        cursor:"pointer",
      }}
    >

      {displayText && (
        <div style={{position:"absolute", inset:0, zIndex:0}}>
          <DigitalRain loadingText={rainText}/>
        </div>
      )}

      {/* smoke layer */}
      <div style={{
        position:"absolute",inset:0,
        background:`url(${smokeGIF}) center / cover no-repeat`,
        mixBlendMode:"screen", opacity:0.5,
        pointerEvents:"none", zIndex:1,
      }}/>

      {/* headline + logo */}
      <div style={{
        position:"absolute", inset:0, display:"flex",
        flexDirection:"column", justifyContent:"center",
        alignItems:"center", pointerEvents:"none", zIndex:2,
      }}>

        {/* Gradient headline */}
        {displayText && (
          <div>
            {(() => {
              const useGradient = canClipText && !hasNonAscii;
              return (
                <span
                  style={{
                    display:"inline-block",
                    whiteSpace:"pre-wrap",
                    fontFamily: hasNonAscii ? FONT_UNICODE : FONT_ASCII,
                    fontSize: headlineFS(view, displayChars.length),
                    background: useGradient ? headlineGradient : "none",
                    backgroundClip: useGradient ? "text" : "border-box",
                    WebkitBackgroundClip: useGradient ? "text" : "border-box",
                    WebkitTextFillColor: useGradient ? "transparent" : "currentColor",
                    color: useGradient ? "transparent" : "white",
                    transition:"opacity 0.4s ease",
                    opacity: fadeText ? 0 : 1,
                    position:"relative",
                    top:LS_LAYOUT[view].headlineTop,
                    left:LS_LAYOUT[view].headlineLeft,
                    right:LS_LAYOUT[view].headlineRight,
                    bottom:LS_LAYOUT[view].headlineBottom,
                  }}
                >
                  {displayText}
                </span>
              );
            })()}
          </div>
        )}

        {/* Falling duplicate copies */}
        {copies.length>0 && (
          <div style={{
            position:"absolute", inset:0, display:"flex",
            justifyContent:"center", alignItems:"center",
            zIndex:3, pointerEvents:"none",
          }}>
            {copies.map((copy,idx)=>(
              <div key={idx} style={{whiteSpace:"nowrap"}}>
                {displayChars.map((ch,i)=>(
                  <span key={i} style={{
                    display:"inline-block",
                    fontSize:copy.fontSize,
                    color: isAsciiPrintable(ch) ? copy.colors[i] : "white",
                    animation:`fallLoop ${copy.duration} linear infinite`,
                    animationDelay:`${copy.delay + i*copy.letterDelay}s`,
                    position:"relative",
                    left:LS_LAYOUT[view].copyLeft,
                    top:LS_LAYOUT[view].copyTop,
                    right:LS_LAYOUT[view].copyRight,
                    bottom:LS_LAYOUT[view].copyBottom,
                  }}>
                    <span style={{
                      display:"inline-block",
                      fontFamily: isAsciiPrintable(ch) ? FONT_ASCII : FONT_UNICODE,
                      fontSize: waveFS(view, displayChars.length),
                      color:"white",
                      opacity: fadeText ? -2 : 1,
                      animation:"wave 1.5s ease-in-out infinite",
                    }}>{vis(ch)}</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* spinning logo */}
        <img src={LOGO} alt="logo" style={{
          width:LS_LAYOUT[view].logoWidth, height:"auto",
          animation:"spin 10s linear infinite",
          transition:"opacity 0.6s ease",
          opacity: showLogo ? 1 : 0,
          position:"relative", bottom:LS_LAYOUT[view].logoBottom,
        }}/>
      </div>

      {/* optional hint */}
      {!skipped && (
        <div
          className="
            position-absolute bottom-0 start-50 translate-middle-x
            bg-dark bg-opacity-75 text-white fw-semibold
            small px-3 py-1 rounded-5
          "
          style={{
            animation: "hintPulse 2.4s ease-in-out infinite",
            marginBottom: "1.5rem",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          Click or Tap to Proceed to the Music
        </div>
      )}

      {/* keyframes */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes wave { 0%,100%{ transform:translateY(0);}50%{ transform:translateY(-6px);} }
        @keyframes fallLoop {
          0%{ transform:translateY(-100vh); opacity:0; }
          50%{ opacity:1; }
          100%{ transform:translateY(110vh); opacity:0; }
        }
        @keyframes hintPulse { 0%,100%{ opacity:.55; } 50%{ opacity:1; } }
      `}</style>
    </div>
  );
}
